import { useEffect, useState } from "react";
import { FlatList, Pressable, Text, View } from "react-native";
import {
  registerWidgetConfigurationScreen,
  type WidgetConfigurationScreenProps,
} from "react-native-android-widget";
import { buildDuranceWidget } from "./DuranceWidgetView";
import { readCountersSnapshot, setWidgetCounterId } from "./widget-storage";
import { lightColors } from "~/styles/tokens";
import type { Counter } from "~/types";

/**
 * A standalone RN root (registered separately from the main app) shown by
 * Android when a widget is placed with `widgetFeatures: "reconfigurable"`.
 * Deliberately plain RN — no app providers — since it runs outside the
 * app's normal navigation/query tree.
 */
function ConfigurationScreen({
  widgetInfo,
  renderWidget,
  setResult,
}: WidgetConfigurationScreenProps) {
  const [counters, setCounters] = useState<Counter[] | null>(null);

  useEffect(() => {
    void readCountersSnapshot().then(setCounters);
  }, []);

  async function choose(counter: Counter) {
    await setWidgetCounterId(widgetInfo.widgetId, counter.id);
    renderWidget(buildDuranceWidget(counter));
    setResult("ok");
  }

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: lightColors.background,
        paddingTop: 56,
      }}
    >
      <Text
        style={{
          fontSize: 20,
          fontWeight: "700",
          color: lightColors.ink,
          paddingHorizontal: 20,
          marginBottom: 4,
        }}
      >
        Choose a counter
      </Text>
      <Text
        style={{
          fontSize: 13,
          color: "#666",
          paddingHorizontal: 20,
          marginBottom: 16,
        }}
      >
        This widget will show its day count.
      </Text>

      {counters === null ? null : counters.length === 0 ? (
        <View style={{ paddingHorizontal: 20 }}>
          <Text style={{ color: "#666" }}>
            Open Durance and add a counter first, then add this widget again.
          </Text>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Cancel"
            onPress={() => setResult("cancel")}
            style={{ marginTop: 24, alignSelf: "flex-start" }}
          >
            <Text style={{ color: lightColors.accent, fontWeight: "600" }}>
              Cancel
            </Text>
          </Pressable>
        </View>
      ) : (
        <FlatList
          data={counters}
          keyExtractor={(item) => item.id}
          ItemSeparatorComponent={() => (
            <View style={{ height: 1, backgroundColor: "#e6e6e6" }} />
          )}
          renderItem={({ item }) => (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={item.label || "Untitled"}
              onPress={() => void choose(item)}
              style={{ paddingVertical: 16, paddingHorizontal: 20 }}
            >
              <Text
                style={{
                  fontSize: 16,
                  fontWeight: "600",
                  color: lightColors.ink,
                }}
              >
                {item.label || "Untitled"}
              </Text>
              <Text style={{ fontSize: 13, color: "#666", marginTop: 2 }}>
                {item.targetDate} ·{" "}
                {item.direction === "up" ? "Since" : "Until"}
              </Text>
            </Pressable>
          )}
        />
      )}
    </View>
  );
}

export function setupWidgetConfigurationScreen(): void {
  registerWidgetConfigurationScreen(ConfigurationScreen);
}
